import { AxiosError, AxiosResponse } from "axios";
import { ResourceEntity, ResourceEntityInput } from "./resources.types";
import { api } from "../api";
import { ResourceType } from "../../types";
import {
  ExtractionJobStatus,
  ResourceResponse,
} from "../../types/api/getResources";

export interface TriggerExtractionData {
  job_id: number;
  status: ExtractionJobStatus;
  replaced_file_ids?: number[];
  message: string;
}

export interface TriggerExtractionActiveJob {
  id: number;
  status: ExtractionJobStatus;
  attempt_count: number;
  created_at: string;
  updated_at: string;
}

export type TriggerExtractionResult =
  | { ok: true; data: TriggerExtractionData }
  | {
      ok: false;
      kind: "conflict";
      active_job: TriggerExtractionActiveJob;
      message: string;
    }
  | {
      ok: false;
      kind: "bad_request" | "not_found" | "unknown";
      message: string;
    };

export const getResources = async (params?: { type?: ResourceType }) => {
  try {
    const response: AxiosResponse<ResourceResponse[]> = await api.get(
      "/resources",
      { params },
    );
    return response.status === 200 ? response.data : [];
  } catch (error: any) {
    console.error(error);
    throw error;
  }
};

/**
 * Trae solo recursos de tipo READING (lecturas). El filtro vive en el backend
 * (`GET /resources/readings`); el selector de lecturas del programa lo usa
 * directo, sin filtrar en el cliente.
 */
export const getReadingResources = async () => {
  try {
    const response: AxiosResponse<ResourceResponse[]> = await api.get(
      "/resources/readings",
    );
    return response.status === 200 ? response.data : [];
  } catch (error: any) {
    console.error(error);
    throw error;
  }
};

export const getResourceById = async (id: number) => {
  try {
    const response: AxiosResponse<ResourceEntity> = await api.get(
      "/resources/" + id,
    );
    return response.status === 200 ? response.data : null;
  } catch (error: any) {
    console.error(error);
    throw error;
  }
};

export const createResource = async (data: ResourceEntityInput) => {
  try {
    const response: AxiosResponse<ResourceEntity> = await api.post(
      "/resources",
      data,
    );

    return response.status === 201 ? response.data : null;
  } catch (error: any) {
    console.error(error);
    throw error;
  }
};

export const updateResource = async (id: number, data: ResourceEntityInput) => {
  try {
    const response: AxiosResponse<ResourceEntity> = await api.patch(
      "/resources/" + id,
      data,
    );

    return response.status === 200 ? response.data : null;
  } catch (error: any) {
    console.error(error);
    throw error;
  }
};

export const triggerResourceExtraction = async (
  id: number,
): Promise<TriggerExtractionResult> => {
  try {
    const response: AxiosResponse<TriggerExtractionData> = await api.post(
      `/resources/${id}/extraction-jobs`,
    );
    return { ok: true, data: response.data };
  } catch (error) {
    const axiosError = error as AxiosError<any>;
    const status = axiosError.response?.status;
    const body = axiosError.response?.data;

    if (status === 409) {
      const inner = body?.message;
      // Nest wraps the payload under "message" — { message: string, active_job: {...} }
      if (inner && typeof inner === "object" && inner.active_job) {
        return {
          ok: false,
          kind: "conflict",
          active_job: inner.active_job as TriggerExtractionActiveJob,
          message: inner.message ?? "Ya hay una extracción en curso",
        };
      }
      return {
        ok: false,
        kind: "conflict",
        active_job: {
          id: 0,
          status: "PENDING",
          attempt_count: 0,
          created_at: "",
          updated_at: "",
        },
        message: "Ya hay una extracción en curso",
      };
    }

    if (status === 400) {
      return {
        ok: false,
        kind: "bad_request",
        message:
          typeof body?.message === "string"
            ? body.message
            : "Este recurso no admite extracción.",
      };
    }

    if (status === 404) {
      return {
        ok: false,
        kind: "not_found",
        message: "Recurso no encontrado.",
      };
    }

    console.error("triggerResourceExtraction error", error);
    return {
      ok: false,
      kind: "unknown",
      message:
        typeof body?.message === "string"
          ? body.message
          : "Error al disparar la extracción.",
    };
  }
};

export const deleteResource = async (id: number) => {
  try {
    const response: AxiosResponse<ResourceEntity> = await api.delete(
      "/resources/" + id,
    );

    return response.status === 200 ? response.data : null;
  } catch (error: any) {
    console.error(error);
    throw error;
  }
};
